// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
import {
  BodyTemplate,
  ContextAttribute,
  DrawnCell,
  InboxEntry,
  KnownInboxEntry,
  MessageBody,
  MessageContext,
  TemplateCell,
} from '@/notification/api/notification-types';
import { reportUndrawableMessage } from '@/notification/utilities/report-undrawable-message';
import { toRecordedTime } from '@/utilities/date-time';
import { DateTime } from 'luxon';

// A slot names one context key: {summary}, {shipment}
const SLOT_PATTERN = /\{(\w+)\}/g;

/** Fills a prose template's slots from the message's context. */
export function toRenderedText(
  template: string,
  context: MessageContext
): string {
  return template.replace(SLOT_PATTERN, (_slot, key: string) =>
    toSlotText(context[key] ?? null)
  );
}

// Prose reads a record by the name it goes by, so a title can name a shipment
// without the context carrying its number twice. Anything with no name to say
// in a sentence reads as nothing rather than as a raw value.
function toSlotText(attribute: ContextAttribute | null): string {
  if (attribute === null) {
    return '';
  }
  if (typeof attribute === 'string') {
    return attribute;
  }
  return toRecordName(attribute);
}

// Names a record the way a reader would say it.
function toRecordName(attribute: ContextAttribute): string {
  if (typeof attribute === 'string') {
    return attribute;
  }
  switch (attribute.kind) {
    case 'location':
      return attribute.location.name;
    case 'shipment':
      return attribute.shipment.shipmentNumber;
    case 'project':
      return attribute.project.name;
    case 'customer':
      return attribute.customer.name;
    case 'fulfillmentOrder':
    case 'purchaseOrder':
    case 'returnOrder':
      return attribute.order.number;
    case 'instant':
    case 'day':
    case 'carrier':
    case 'items':
    case 'shipments':
    case 'source':
      return '';
    default:
      return toUnrenderableKind(attribute);
  }
}

function toUnrenderableKind(attribute: never): string {
  reportUndrawableMessage(
    'Inbox message carries an attribute kind prose cannot render',
    'unknown',
    attribute
  );
  return '';
}

/**
 * Renders a stored body template against the context it was written with.
 *
 * Cells name context attributes, so this resolves rather than substitutes: the
 * drawn cell carries the attribute itself, and whatever draws it decides how an
 * instant or a list of items looks.
 */
export function toRenderedBody(
  template: BodyTemplate | undefined,
  context: MessageContext
): MessageBody | null {
  if (template === undefined) {
    return null;
  }
  return {
    summary: toRenderedText(template.summary, context),
    columns: template.columns.map((column) => toDrawnColumn(column, context)),
    details: toDrawnColumn(template.details, context),
  };
}

// A cell the message carries nothing for draws nothing at all, so it is dropped
// here rather than rendered as a hole. A heading over nothing is noise, so a
// column left with no values drops to the blank space that keeps the columns
// beside it where they are.
function toDrawnColumn(
  cells: TemplateCell[],
  context: MessageContext
): DrawnCell[] {
  const drawn = cells
    .map((cell) => toDrawnCell(cell, context))
    .filter((cell) => cell.isText || cell.value !== null);
  return drawn.some((cell) => !cell.isText) ? drawn : [];
}

function toDrawnCell(
  template: TemplateCell,
  context: MessageContext
): DrawnCell {
  const value = context[template.key] ?? null;
  return {
    key: template.key,
    isText: template.key === '',
    variant: template.variant,
    maxEntries: template.maxEntries,
    isDateOnly: template.isDateOnly ?? false,
    // A label is prose too: a cell names whichever milestone fired, or
    // whichever of the three order kinds the shipment carries
    label: toRenderedText(template.label, context),
    note:
      template.offsetFrom === undefined
        ? toRenderedText(template.note ?? '', context)
        : toOffset(value, context[template.offsetFrom] ?? null, template.note),
    value: isEmptyAttribute(value) ? null : value,
  };
}

/**
 * Says how far a milestone was off what it was measured against.
 *
 * Both ends read in the milestone's own zone, because measuring each in its own
 * subtracts days from two different calendars: a delivery at 22:00 in Los
 * Angeles and a receipt an hour later in Tokyo are the same day in one calendar
 * and a day apart in two.
 *
 * A day it was expected on reads as early or late. Another milestone reads as
 * how long after, because nothing records when a receipt was expected.
 */
function toOffset(
  value: ContextAttribute | null,
  measuredAgainst: ContextAttribute | null,
  // What the value is measured against, named the way a reader would say it,
  // for a milestone measured against another milestone rather than a due date
  followedName: string | undefined
): string {
  if (
    value === null ||
    typeof value === 'string' ||
    value.kind !== 'instant' ||
    measuredAgainst === null ||
    typeof measuredAgainst === 'string'
  ) {
    return '';
  }
  const at = toRecordedTime(value.at, value.timezone);
  if (measuredAgainst.kind === 'day') {
    // A day carries no zone, so it is read on the milestone's own calendar
    const expected = measuredAgainst.on.setZone(value.timezone, {
      keepLocalTime: true,
    });
    return toExpectationOffset(toDaysApart(at, expected));
  }
  if (measuredAgainst.kind === 'instant') {
    const followed = toRecordedTime(measuredAgainst.at, value.timezone);
    const days = toDaysApart(at, followed);
    if (days <= 0) {
      return '';
    }
    // Naming what it followed reads better than "later"
    return followedName === undefined
      ? `${toDays(days)} later`
      : `${toDays(days)} after ${followedName}`;
  }
  return '';
}

// Counts whole calendar days between two moments read on one calendar. An hour
// count divided by 24 loses a day to every spring-forward it spans.
function toDaysApart(at: DateTime, measuredAgainst: DateTime): number {
  return Math.trunc(
    at.startOf('day').diff(measuredAgainst.startOf('day'), 'days').days
  );
}

/** Words how far a milestone was off the day it was expected on. */
function toExpectationOffset(days: number): string {
  if (days === 0) {
    return 'on time';
  }
  return days < 0 ? `${toDays(-days)} early` : `${toDays(days)} late`;
}

/** Words a whole number of days. */
function toDays(days: number): string {
  return days === 1 ? '1 day' : `${days.toString()} days`;
}

/** Reports whether an attribute says nothing a reader could see. */
function isEmptyAttribute(attribute: ContextAttribute | null): boolean {
  if (attribute === null) {
    return true;
  }
  if (typeof attribute === 'string') {
    return attribute === '';
  }
  if (attribute.kind === 'items') {
    return attribute.items.length === 0;
  }
  if (attribute.kind === 'shipments') {
    return attribute.entries.length === 0;
  }
  return false;
}

/**
 * Keeps the messages the screen can draw, and reports the ones it cannot.
 *
 * An empty title means the server could not resolve the stored template pairing.
 * The response keeps that entry for cursor reads and the dropped-message count;
 * filtering here reports it through the per-session throttle. A template naming
 * a key the context does not carry is also undrawable.
 */
export function toDrawableMessages(entries: InboxEntry[]): KnownInboxEntry[] {
  const drawable: KnownInboxEntry[] = [];
  for (const entry of entries) {
    if (entry.titleTemplate === '') {
      reportUndrawableMessage(
        'Inbox message has no compatible template',
        entry.key,
        entry.messageType
      );
      continue;
    }
    if (!entry.isKnownType) {
      reportUndrawableMessage(
        'Inbox message carries a type this bundle does not know',
        entry.key,
        entry.messageType
      );
      continue;
    }
    if (entry.context === null) {
      reportUndrawableMessage(
        'Inbox message carries an invalid context for a known type',
        entry.key,
        entry.messageType
      );
      continue;
    }
    const missing = toMissingKey(entry);
    if (missing !== null) {
      reportUndrawableMessage(
        'Inbox message names a context key it does not carry',
        entry.key,
        missing
      );
      continue;
    }
    drawable.push(entry);
  }
  return drawable;
}

// Names the first key the message cannot answer for, or nothing when it can
// answer for all of them.
function toMissingKey(entry: KnownInboxEntry): string | null {
  const prose = [entry.titleTemplate, entry.bodyTemplate?.summary ?? ''];
  const template = entry.bodyTemplate;
  const cells = [
    ...(template?.columns.flat() ?? []),
    ...(template?.details ?? []),
  ];
  for (const cell of cells) {
    if (cell.key === '') {
      continue;
    }
    prose.push(cell.label, cell.note ?? '');
    if (!Object.hasOwn(entry.context, cell.key)) {
      return cell.key;
    }
    if (
      cell.offsetFrom !== undefined &&
      !Object.hasOwn(entry.context, cell.offsetFrom)
    ) {
      return cell.offsetFrom;
    }
  }
  for (const template of prose) {
    for (const [, key] of template.matchAll(SLOT_PATTERN)) {
      if (!Object.hasOwn(entry.context, key)) {
        return key;
      }
    }
  }
  return null;
}
